import React from 'react';
import { auth } from '../../../services/firebase';
import { getTimelineEntryDate } from '../../../services/timeline/dateUtils';
import { buildMoodDocId } from '../../../services/moodService';
import { getMoodOption } from '../../../constants/moodOptions';
import { USER_ROLES } from '../../../constants/roles';
import { LOG_TYPES } from '../../../constants/logTypeRegistry';

export const timelineUserRoleOptions = [
  { value: USER_ROLES.CARE_OWNER, label: 'Care Owner' },
  { value: USER_ROLES.CARE_PARTNER, label: 'Care Partner' },
  { value: USER_ROLES.CAREGIVER, label: 'Caregiver' },
  { value: USER_ROLES.THERAPIST, label: 'Therapist' },
];

export const TEXT_PRIMARY = '#1F2937';

export const getCurrentUserDisplayLabel = () => (
  auth.currentUser?.displayName
  || auth.currentUser?.email?.split('@')[0]
  || auth.currentUser?.email
  || 'User'
);

export const buildOptimisticMoodEntry = ({ childId, moodValue, moodOption }) => {
  const now = new Date();
  const moodDocId = buildMoodDocId(childId, now);
  const userLabel = getCurrentUserDisplayLabel();

  return {
    id: moodDocId,
    childId,
    collection: 'dailyCare',
    actionType: 'mood',
    type: 'mood',
    timelineType: 'mood',
    category: 'mood',
    logCategory: 'mood',
    timestamp: now,
    createdAt: now,
    updatedAt: now,
    value: moodValue,
    moodValue,
    mood: moodValue,
    title: 'Mood',
    titlePrefix: 'Mood',
    content: moodValue,
    text: moodValue,
    label: 'Mood',
    data: {
      level: moodValue,
      value: moodValue,
      source: 'mobile-dashboard',
    },
    icon: null,
    iconComponent: moodOption.iconComponent || null,
    color: LOG_TYPES.mood.palette.dot,
    categoryIcon: null,
    categoryIconComponent: moodOption.iconComponent || null,
    categoryColor: LOG_TYPES.mood.palette.dot,
    incidentStyle: false,
    entryType: 'mood',
    incidentCategoryId: 'mood',
    incidentCategoryLabel: 'Mood',
    incidentCategoryColor: LOG_TYPES.mood.palette.dot,
    incidentCategoryIcon: null,
    incidentCategoryIconComponent: moodOption.iconComponent || null,
    authorName: userLabel,
    loggedByUser: userLabel,
  };
};

export const getEntryDate = (entry) => {
  if (!entry) {
    return null;
  }

  const candidate = getTimelineEntryDate(entry)
    || entry.updatedAt?.toDate?.()
    || entry.updatedAt
    || (typeof entry.timestamp?.toDate === 'function' ? entry.timestamp.toDate() : null)
    || new Date(entry.timestamp);

  return candidate instanceof Date && !Number.isNaN(candidate.getTime()) ? candidate : null;
};

export const getMoodSnapshot = (entries = []) => {
  const latestMoodEntry = [...entries]
    .map((entry) => ({ entry, date: getEntryDate(entry) }))
    .filter(({ date, entry }) => Boolean(date) && entry?.status !== 'deleted')
    .sort((a, b) => b.date - a.date)
    .map(({ entry }) => entry)
    .find((entry) => {
      const category = String(entry?.category || entry?.type || entry?.timelineType || entry?.actionType || entry?.logCategory || '').toLowerCase();
      const text = String(
        entry?.moodValue
        || entry?.value
        || entry?.data?.level
        || entry?.content
        || entry?.text
        || entry?.notes
        || ''
      ).toLowerCase();
      return category === 'mood'
        || category === 'mood_log'
        || text.includes('mood')
        || text.includes('calm')
        || text.includes('happy')
        || text.includes('neutral')
        || text.includes('frustrated');
    });

  const rawMood = String(
    latestMoodEntry?.moodValue
    || latestMoodEntry?.data?.level
    || latestMoodEntry?.value
    || latestMoodEntry?.mood
    || latestMoodEntry?.moodLevel
    || latestMoodEntry?.content
    || latestMoodEntry?.text
    || latestMoodEntry?.notes
    || 'Calm'
  ).trim();
  const matchedMood = getMoodOption(rawMood);

  return {
    hasMood: Boolean(latestMoodEntry),
    key: matchedMood.key,
    iconComponent: matchedMood.iconComponent || null,
    label: matchedMood.label,
    raw: rawMood,
    updatedAt: getEntryDate(latestMoodEntry) || latestMoodEntry?.updatedAt || latestMoodEntry?.timestamp || latestMoodEntry?.createdAt || null,
  };
};

export const renderQuickTagChipIcon = (tag) => {
  if (tag?.iconComponent) {
    const IconComponent = tag.iconComponent;
    return <IconComponent sx={{ fontSize: 16, color: 'inherit' }} />;
  }

  if (tag?.icon) {
    return <span aria-hidden="true">{tag.icon}</span>;
  }

  return null;
};
